/**
 * Conflict Detail / Schedule Overlap display formatting, shared by the client
 * grid cells and the api xlsx export. Browser-safe: no luxon, db, or logging
 * imports (the client deep-imports this module, never the package barrel).
 * Window computation lives in packages/api/src/common/conflictWindows.ts.
 */

export type ConflictWindow = {
  counts: { moratoria: number; paving: number; cip: number };
  overlapStartMs: number;
  overlapEndMs: number;
};

/** "1 Moratorium · 2 Paving · 1 CIP" — zero-count segments omitted; '' when all zero. */
export function formatConflictDetail(counts: ConflictWindow['counts']): string {
  return [
    counts.moratoria > 0 && `${counts.moratoria} ${counts.moratoria === 1 ? 'Moratorium' : 'Moratoria'}`,
    counts.paving > 0 && `${counts.paving} Paving`,
    counts.cip > 0 && `${counts.cip} CIP`,
  ]
    .filter(Boolean)
    .join(' · ');
}

// Input millis are UTC midnights (construction dates and feature plan dates are
// both parsed as UTC date-only values); UTC keeps labels zone-stable.
const monthLabel = (ms: number) =>
  new Date(ms).toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

const monthIndex = (ms: number) => {
  const d = new Date(ms);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
};

/**
 * The overlap window rounded to whole months, inclusive ("May 15 – Aug 10" →
 * "May 2026 – Aug 2026"), split into the duration-chip label ("4 Mo.", the
 * inclusive month count: May–Jun = "2 Mo.", same month = "1 Mo.") and the
 * month range text.
 */
export function formatScheduleOverlapParts(window: ConflictWindow): { duration: string; range: string } {
  const startLabel = monthLabel(window.overlapStartMs);
  const endLabel = monthLabel(window.overlapEndMs);
  if (startLabel === endLabel) return { duration: '1 Mo.', range: startLabel };
  const months = monthIndex(window.overlapEndMs) - monthIndex(window.overlapStartMs) + 1;
  return { duration: `${months} Mo.`, range: `${startLabel} – ${endLabel}` };
}

/** Single-string form of the overlap window ("May 2026 – Aug 2026 · 3 mo.") — exports and child rows. */
export function formatScheduleOverlap(window: ConflictWindow): string {
  const { duration, range } = formatScheduleOverlapParts(window);
  return `${range} · ${duration.toLowerCase()}`;
}
